/**
 * Only use this prop converter for deprecated-react-charts charts. For the new charting library's charts,
 * use the converters in prop-converters or create a new one.
 */
import {DataType} from 'data-frames/DataFrameTypes';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {
  Continuous2DChartParameters,
  VisualizationType,
} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import {Continuous2DChartProps} from 'insights/analytics-visualizations/viewers/deprecated-react-charts/Continuous2DChart';
import {XAxisScale} from 'insights/analytics-visualizations/viewers/deprecated-react-charts/XAxisScale';
import maybeConvertProps from 'insights/analytics-visualizations/viewers/prop-converters/maybeConvertProps';
import findAxisColumn from 'insights/analytics-visualizations/viewers/prop-converters/utils/findAxisColumn';
import {RunQueryOutputLegacyData} from 'insights/warehouse/AnalyticsWarehouseTypes';
import {assertNever} from 'util/Types';

import {ConversionOutput, SuccessfulConversionOutput} from '../PropConverterTypes';
import convertToBaseChartProps from './convertToBaseChartProps';

/**
 * Converts Continuous2DChartParameters from system name based parameters to display name based parameters.
 *
 * This is currently only used for LineChartViewer, since that is the only visualization viewer that still
 * uses legacy data frames.
 */
function getDisplayNameBasedParameters(
  dataFrame: DataFrame,
  parameters: Continuous2DChartParameters
): Continuous2DChartParameters {
  const xAxisColumnName = findAxisColumn(dataFrame, parameters.xAxisColumnName).displayName;
  const yAxisColumnName = findAxisColumn(dataFrame, parameters.yAxisColumnName).displayName;
  const seriesColumnName =
    parameters.seriesColumnName != null
      ? findAxisColumn(dataFrame, parameters.seriesColumnName).displayName
      : null;

  return {
    ...parameters,
    xAxisColumnName,
    yAxisColumnName,
    seriesColumnName,
  };
}

export default function maybeConvertToContinuous2DChartProps(
  dataFrame: DataFrame,
  runQueryOutput: RunQueryOutputLegacyData,
  parameters: Continuous2DChartParameters,
  visualizationType: VisualizationType
): ConversionOutput<Continuous2DChartProps> {
  return maybeConvertProps(() =>
    convertToContinuous2DChartProps(dataFrame, runQueryOutput, parameters, visualizationType)
  );
}

export function convertToContinuous2DChartProps(
  dataFrame: DataFrame,
  runQueryOutput: RunQueryOutputLegacyData,
  parameters: Continuous2DChartParameters,
  visualizationType: VisualizationType
): SuccessfulConversionOutput<Continuous2DChartProps> {
  const displayNameBasedParameters = getDisplayNameBasedParameters(dataFrame, parameters);
  const conversionOutput = convertToBaseChartProps(
    runQueryOutput,
    displayNameBasedParameters,
    [DataType.NUMBER, DataType.DATE_OR_TIMESTAMP, DataType.STRING],
    visualizationType
  );

  const baseChartProps = {
    yAxisIncludesZero: parameters.yAxisIncludesZero,
  };

  switch (conversionOutput.xAxisType) {
    case DataType.NUMBER:
      return {
        valid: true,
        props: {...conversionOutput.props, ...baseChartProps, xAxisScale: XAxisScale.LINEAR},
      };
    case DataType.DATE_OR_TIMESTAMP:
      return {
        valid: true,
        props: {...conversionOutput.props, ...baseChartProps, xAxisScale: XAxisScale.TIME},
      };
    case DataType.STRING:
      return {
        valid: true,
        props: {...conversionOutput.props, ...baseChartProps, xAxisScale: XAxisScale.CATEGORICAL},
      };
    default:
      throw new Error(`Unexpected axis type: ${assertNever(conversionOutput)}`);
  }
}
